import { createFactory, memo } from 'react'
import React from 'react'
import ReactDOM from 'react-dom'
import CsgModalConfirmation from 'components/organisms/ModalConfirmation'
import dayjs from 'dayjs'

import PropTypes from 'prop-types'
import * as R from 'ramda'
import { differenceInDays, isLeapYear, parseISO } from 'date-fns'
import dateFnsFormat from 'date-fns/format'
import { withProps, branch, withState } from 'recompose'
import i18n from './i18n'
import { useMediaQuery, useTheme } from '@material-ui/core'

export const redirectTo = defaultPath => ({ history }) => path => history.push(path || defaultPath)

const getMessageFromException = errorMessage => {
  var result = errorMessage.match(/.*Exception: (.*)/i)
  return result ? result[1] : false
}

const getMessageFromGrupoValidacaoException = errorMessage => {
  var result = errorMessage.includes('Grupo de Validação:')
  return result ? errorMessage : false
}

export const getParamKeyByLayoutType = {
  CONSIGNACAO_MENSAL: () => 'parametrosConsignacaoMensal',
  CONFERENCIA_FECHAMENTO: () => 'parametrosFechamento',
  ARQUIVO_MARGEM: () => 'parametrosArquivoMargem',
  RELATORIO_CONTRATOS_PERIODO: () => 'parametrosContratosPeriodo',
  ARQUIVO_SITUACAO_FUNCIONAL: () => 'parametrosArquivoSituacaoFuncional'
}

export const hasValue = R.compose(R.not, R.either(R.isNil, R.isEmpty))

export const allHasValue = R.all(hasValue)

export const compose = (...fns) => Component => fns.reduceRight((acc, fn) => fn(acc), Component)

function normalizeKey(key) {
  return key.replaceAll(':', '')
}

export const withRestErrorHandler = () => Component => {
  const factory = createFactory(Component)

  return memo(props =>
    factory({
      ...props,
      tRestError: key => {
        if (!key) {
          return props.t(`rest_errors.default`)
        }

        const hasErrorException = getMessageFromException(key)
        if (hasErrorException) return hasErrorException

        const hasGrupoValidacaoErrorException = getMessageFromGrupoValidacaoException(key)
        if (hasGrupoValidacaoErrorException) return hasGrupoValidacaoErrorException

        key = normalizeKey(key)
        const translationResult = props.t(`rest_errors.${key}`)
        const translationNotFound = translationResult.includes('rest_errors')

        return translationNotFound ? key : translationResult
      }
    })
  )
}

export const getRestListData = response => {
  const data = R.when(R.has('data'), R.prop('data'))
  const embedded = R.when(R.has('_embedded'), R.prop('_embedded'))
  const getFirstKey = object => R.compose(R.prop(R.__, object), R.head, R.keys)(object)

  if (!R.has('data', response) && !R.has('_embedded', response)) {
    return []
  }

  return R.compose(getFirstKey, embedded, data)(response)
}

export const colors = [
  'common',
  'background',
  'primary',
  'secondary',
  'info',
  'success',
  'purple',
  'error',
  'warning',
  'text',
  'grey'
]

export const colorPropType = PropTypes.oneOf(colors)

export const moneyToFloat = R.compose(
  parseFloat,
  val => `${val.substr(0, val.length - 2)}.${val.substr(-2)}`,
  R.replace(/[^\d]/g, '')
)

export const stringToNumber = R.compose(parseFloat, R.replace(',', '.'))

export const defer = (fn, time = 0) => setTimeout(fn, time)

export const dateToFolha = date => parseInt(dateFnsFormat(date, 'yyyyMM'))

export const folhaToDate = folha => {
  const year = folha.toString().substr(0, 4)
  const month = folha.toString().substr(-2)

  return new Date(year, month - 1, 1)
}

export const titlealize = str => str.toLowerCase().replace(/(?:^|\s)\S/g, val => val.toUpperCase())

export const getFileExtension = filename => filename.split('.').pop()

export const formatExtensionFile = fileName => {
  const type = getFileExtension(fileName)
  return formatExtension(type)
}

export const formatExtension = extension => {
  if (extension !== null) {
    return `.${extension.toLowerCase()}`
  }
  return
}

export const getFileExtensionByBase64 = base64 => {
  const char = base64.charAt(0)

  const extension = {
    '/': () => 'jpg',
    i: () => 'png',
    J: () => 'pdf',
    Default: () => 'txt'
  }

  return (extension[char] || extension['Default'])()
}

export const downloadFile = (filename, base64) => {
  const type = getFileExtensionByBase64(base64)

  download(filename, base64, type)
}

export const downloadFileWithExtension = (filename, base64, extension) => {
  download(filename, base64, extension)
}

export const download = (name, base64, type) => {
  const url = `data:application/${type};base64,${base64}`
  const fileName = `${name}.${type}`

  downloadLink(fileName, url)
}

export const downloadPDF = (name, pdfBase64) => {
  const url = `data:application/pdf;base64,${pdfBase64}`
  const fileName = `${name}.pdf`

  downloadLink(fileName, url)
}

export const downloadLink = (name, url) => {
  const element = document.createElement('a')

  element.href = url
  element.download = name
  element.click()
}

export const verifyOrRules = R.curry((rules, authorities = []) => {
  const userRules = R.map(rule => (rule.authority ? rule.authority : rule), authorities)
  const arrRules = Array.isArray(rules) ? rules : [rules]

  return !!R.intersection(userRules, arrRules).length
})

export const verifySessionIsOr = R.curry((types, session = {}) => {
  const arrTypes = Array.isArray(types) ? types : [types]
  return arrTypes.filter(type => session[type]).length
})

export const verifySessionIs = R.curry((type, session = {}) => session[type])

export const verifyHasRules = R.curry((rules, authorities = []) => {
  const userRules = R.map(rule => (rule.authority ? rule.authority : rule), authorities)
  const arrRules = Array.isArray(rules) ? rules : [rules]

  return R.intersection(userRules, arrRules).length === arrRules.length
})

export const verifyRuleUpdateOrView = R.curry((rule, authorities, { view, update }) => {
  if (verifyHasRules(rule.concat('.EDIT'), authorities)) {
    typeof update === 'function' && update()
    return
  }
  if (verifyHasRules(rule, authorities)) {
    typeof view === 'function' && view()
    return
  }
})

export const range = R.curry((value, finalValue) => {
  const recursiveRange = (value, array) => {
    if (value > finalValue) return array
    return recursiveRange(R.inc(value), R.append(value, array))
  }

  return recursiveRange(value, [])
})

export const toTwoDigits = R.compose(R.when(R.gt(10), R.concat('0')), R.toString)

export const toFixedTrunc = (x, n = 2) => {
  const v = (typeof x === 'string' ? x : x.toString()).split('.')
  if (n <= 0) return v[0]
  let f = v[1] || ''
  if (f.length > n) return `${v[0]}.${f.substr(0, n)}`
  while (f.length < n) f += '0'
  return `${v[0]}.${f}`
}

export const buildUrl = url => (concat = '') =>
  concat ? R.compose(R.join('/'), R.prepend(url), R.append(R.__, R.always([])))(concat) : url

export const fileDataToBlob = (data, type) => {
  const rawLength = data.length
  const blobArray = new Uint8Array(new ArrayBuffer(rawLength))

  for (let i = 0; i < rawLength; i++) {
    blobArray[i] = data.charCodeAt(i)
  }

  return new Blob([blobArray], { type: `application/${type}` })
}

export const fileBase64ToBlob = (base64, type) => {
  const raw = window.atob(base64)
  return fileDataToBlob(raw, type)
}

export const getBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result.substr(reader.result.indexOf('base64,') + 7))
    reader.onerror = error => reject(error)
  })

export const currencyToNumber = R.compose(
  Number,
  R.replace(',', '.'),
  R.replace('.', ''),
  R.replace(' ', ''),
  R.replace('R$', '')
)

export const percentCommaToPoint = R.compose(Number, R.replace(',', '.'), R.replace('.', ''), R.replace(' ', ''))

export const floatToComma = R.compose(R.replace('.', ','), R.toString)

export const excludeZeros = R.compose(
  //exclui somente os zeros iniciais
  R.replace(/^[0|\D]*/, '')
)

export const formatStatus = contratos => {
  if (contratos) {
    contratos.forEach(contrato => {
      const status = contrato.status

      if (status === 'AG_DEFERIMENTO' && contrato.idContratoCompra) {
        if (contrato.idConvenio === contrato.idConvenioContratoCompra) {
          contrato.status = `${status}_REFIN`
        } else {
          contrato.status = `${status}_PORTABILIDADE`
        }
      }
    })
  }
}

export const isValidDate = date => date instanceof Date && !isNaN(date)

export const removeSlashAndReverseFolhaPrimeiroDesconto = folha => {
  if (folha instanceof Date) {
    folha = dateFnsFormat(folha, 'MM/yyyy')
  }

  const pieces = folha.split('/')
  return pieces[1] + pieces[0]
}

export const openPDF = arquivo => {
  const blob = fileBase64ToBlob(arquivo, 'pdf')
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    return window.navigator.msSaveOrOpenBlob(blob, 'comprovante.pdf')
  }

  return downloadPDF('comprovante.pdf', arquivo)
}

export const fileToBase64 = file =>
  new Promise(resolve => {
    var reader = new FileReader()
    reader.onload = R.compose(resolve, R.nth(1), R.split(','), R.path(['target', 'result']))
    reader.readAsDataURL(file)
  })

export const roundTwoDecimals = value => Math.round(value * 100) / 100

export const yearMounthToMonthYear = date => `${date.toString().substr(4, 2)}/${date.toString().substr(0, 4)}`

export const withDefaultProps = (prop, fnValue) =>
  branch(
    props => !props[prop],
    withProps(props => ({ [prop]: fnValue(props) }))
  )

export const withListProps = R.compose(
  withState('totalElements', 'setTotalElements', 0),
  withState('currentPage', 'setCurrentPage', 0),
  withState('searchValue', 'setSearchValue', '')
)

export const createFolha = (month, year) => `${year}${toTwoDigits(month)}`

export const formatFolhaToUser = date => (date ? `${date.toString().substr(4, 2)}/${date.toString().substr(0, 4)}` : '')

export const formatFolhaByDateToUser = (month, year) => `${month}/${year}`

export const fullYearMonthToMonth = fullYearMonth => {
  const month = fullYearMonth.toString().substr(4, 2)

  return Number(month)
}

export const formatCnpjToNumber = R.compose(
  String,
  R.replace('-', ''),
  R.replace('/', ''),
  R.replace('.', ''),
  R.replace('.', '')
)

export const formatCpfToNumber = R.compose(String, R.replace('-', ''), R.replace('.', ''), R.replace('.', ''))

export const formatTelefoneToNumber = R.compose(
  String,
  R.replace('-', ''),
  R.replace(' ', ''),
  R.replace(' ', ''),
  R.replace(')', ''),
  R.replace('(', '')
)

export const zipCodeToNumber = R.compose(String, R.replace('-', ''))

export function restrictToNumberAndDots(value) {
  return value.replace(/[^0-9\\.]/g, '')
}

export function restrictToValidRgDigits(value) {
  if (value) {
    return value.replace(/[^0-9]/g, '')
  }
}

export function validaCpf(value) {
  var cpf = formatCpfToNumber(value)
  if (cpf === '') return false
  // Elimina CPFs invalidos conhecidos
  if (
    cpf.length !== 11 ||
    cpf === '00000000000' ||
    cpf === '11111111111' ||
    cpf === '22222222222' ||
    cpf === '33333333333' ||
    cpf === '44444444444' ||
    cpf === '55555555555' ||
    cpf === '66666666666' ||
    cpf === '77777777777' ||
    cpf === '88888888888' ||
    cpf === '99999999999'
  )
    return false
  // Valida 1o digito
  let add = 0
  let rev = 0

  for (var i = 0; i < 9; i++) add += parseInt(cpf.charAt(i)) * (10 - i)
  rev = 11 - (add % 11)
  if (rev === 10 || rev === 11) rev = 0
  if (rev !== parseInt(cpf.charAt(9))) return false
  // Valida 2o digito
  add = 0
  for (i = 0; i < 10; i++) add += parseInt(cpf.charAt(i)) * (11 - i)
  rev = 11 - (add % 11)
  if (rev === 10 || rev === 11) rev = 0
  if (rev !== parseInt(cpf.charAt(10))) return false
  return true
}

export function validaTelefone(value) {
  var telefone = formatTelefoneToNumber(value)

  return telefone.length >= 10
}

export const replaceNewLines = R.when(hasValue, R.replace(/\n/g, '<br />'))

export function getSecondPart(element, excludeChar) {
  if (element.includes(excludeChar)) {
    return element.split(excludeChar)[1]
  } else {
    return element
  }
}

export const UserNavigationBlocker = (message, callback) => {
  const node = document.createElement('div')

  node.setAttribute('csg-confirm-modal', '')

  const shouldOpen = answer => {
    ReactDOM.unmountComponentAtNode(node)
    callback(answer)
  }

  const handleConfirm = callbackState => {
    ReactDOM.unmountComponentAtNode(node)
    callback(callbackState)
  }

  const handleCancel = () => {
    ReactDOM.unmountComponentAtNode(node)
    callback()
  }

  document.body.appendChild(node)

  ReactDOM.render(
    <CsgModalConfirmation
      open={shouldOpen}
      message={message}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      bgColor="primary"
    />,
    node
  )
}

export const areTheyTheSameObjects = (firstObject, secondObject) => {
  const firstObjectKey = Object.keys(firstObject)
  const secondObjectKey = Object.keys(secondObject)
  if (firstObjectKey.length !== secondObjectKey.length) {
    return false
  }
  for (let key of firstObjectKey) {
    if (firstObject[key] !== secondObject[key]) {
      return false
    }
  }
  return true
}

export const areArraysWithSameObjects = (array1, array2) => {
  if (array1.length !== array2.length) {
    return false
  }

  for (let i = 0; i < array1.length; i++) {
    if (!areTheyTheSameObjects(array1[i], array2[i])) {
      return false
    }
  }

  return true
}

export const capitalizeText = text => {
  const words = text.toLowerCase().split(' ')

  const capitalizedWords = words.map(word => {
    return word.charAt(0).toUpperCase() + word.slice(1)
  })

  const capitalizedText = capitalizedWords.join(' ')

  return capitalizedText
}

export const maskPhoneNumber = phoneNumber => {
  const cleaned = ('' + phoneNumber).replace(/\D/g, '')

  if (cleaned.startsWith('08')) {
    const match = cleaned.match(/^(\d{4})(\d{3})(\d{4})$/)
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`
    }
  }

  const match = cleaned.match(/^(\d{2})(\d{4,5})(\d{4})$/)

  if (match) {
    const formattedPhoneNumber =
      match[2].length === 4
        ? `(${match[1]}) ${match[2]}-${match[3]}`
        : `(${match[1]}) ${match[2].slice(0, 1)} ${match[2].slice(1)}-${match[3]}`

    return formattedPhoneNumber
  }

  return phoneNumber
}

export function getRemainingTime(timestampsInMiliseconds) {
  const timestampDayjs = dayjs(timestampsInMiliseconds)
  const nowDayjs = dayjs()

  if (timestampDayjs.isBefore(nowDayjs)) {
    return {
      seconds: '00',
      minutes: '00'
    }
  }

  const seconds = getRemainingSeconds(nowDayjs, timestampDayjs)
  const minutes = getRemainingMinutes(nowDayjs, timestampDayjs)

  return {
    seconds,
    minutes
  }
}

function getRemainingSeconds(nowDayjs, timestampDayjs) {
  const seconds = timestampDayjs.diff(nowDayjs, 'seconds') % 60
  return formatTime(seconds)
}

function getRemainingMinutes(nowDayjs, timestampDayjs) {
  const minutes = timestampDayjs.diff(nowDayjs, 'minutes') % 60
  return formatTime(minutes)
}

function formatTime(number) {
  const formattedTime = String(number).padStart(2, '0')

  return formattedTime
}

export const replaceNullsWithEmpty = obj => {
  if (obj === null) {
    return ''
  }

  if (Array.isArray(obj)) {
    return obj.map(item => replaceNullsWithEmpty(item))
  }

  if (typeof obj === 'object') {
    const newObj = {}
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        newObj[key] = replaceNullsWithEmpty(obj[key])
      }
    }
    return newObj
  }

  return obj
}

export const convertDateNumberToDate = dateNumber => {
  const dateString = dateNumber.toString()

  const dayPart = dateString.length === 6 ? '01' : dateString.slice(6, 8)

  const parsedDate = parseISO(`${dateString.slice(0, 4)}-${dateString.slice(4, 6)}-${dayPart}T00:00:00`)

  const formattedDate = dateFnsFormat(parsedDate, "yyyy-MM-dd'T'HH:mm:ss")

  return formattedDate
}

export const isWithinMonthRange = (startDate, endDate, maxMonthsDifference) => {
  if (!startDate || !endDate) return false

  const daysLimit = isLeapYear(startDate) ? maxMonthsDifference * 30.5 : maxMonthsDifference * 30.4375
  const daysDiff = differenceInDays(endDate, startDate)

  return daysDiff <= Math.floor(daysLimit)
}

const currencyType = {
  'pt-BR': 'BRL',
  'en-US': 'USD',
  'es-ES': 'EUR'
}

export function currencyFormatter(value) {
  value = value || 0

  const options = { style: 'currency', currency: currencyType[i18n.language] || 'BRL', minimumFractionDigits: 2 }
  return new Intl.NumberFormat(i18n.language, options).format(value)
}

export function normalizeAllSpacesAccentsSpecialCharacter(value) {
  value = value || ''
  const regexAllSpaces = /\s/g
  const regexAllAccents = /[\u0300-\u036f]/g
  const regexAllSpecialCharacters = /\W/g
  return value
    .normalize('NFD')
    .replace(regexAllSpaces, '')
    .replace(regexAllAccents, '')
    .replace(regexAllSpecialCharacters, '')
    .toLowerCase()
}

export function currencySIConverter(value) {
  const abbreviations = ['', 'k', 'M', 'G', 'T']
  const log1000 = Math.floor(Math.log10(Math.abs(value)) / 3)
  const index = Math.max(0, Math.min(log1000, abbreviations.length - 1))

  const convertedNumber = value / Math.pow(1000, index)

  const formattedNumber = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: currencyType[i18n.language] || 'BRL',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }).format(convertedNumber)

  return `${formattedNumber} ${abbreviations[index]}`
}

export const parseBoolean = value => value && value.toLowerCase() === 'true'

export const isEmptyOrFalse = value => !value || value === false

export function validaCNPJ(cnpj) {
  cnpj = cnpj.replace(/[^\d]+/g, '')
  if (cnpj.length !== 14) return false

  // Elimina CNPJs inválidos conhecidos (sequências repetidas)
  if (/^(\d)\1+$/.test(cnpj)) return false

  // Calcula os dígitos verificadores
  let tamanho = cnpj.length - 2
  let numeros = cnpj.substring(0, tamanho)
  let digitos = cnpj.substring(tamanho)
  let soma = 0
  let pos = tamanho - 7

  // Calcula o primeiro dígito verificador
  for (let i = tamanho; i >= 1; i--) {
    soma += numeros.charAt(tamanho - i) * pos--
    if (pos < 2) pos = 9
  }
  let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11)
  if (resultado !== parseInt(digitos.charAt(0))) return false

  // Calcula o segundo dígito verificador
  tamanho++
  numeros = cnpj.substring(0, tamanho)
  soma = 0
  pos = tamanho - 7
  for (let i = tamanho; i >= 1; i--) {
    soma += numeros.charAt(tamanho - i) * pos--
    if (pos < 2) pos = 9
  }
  resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11)
  if (resultado !== parseInt(digitos.charAt(1))) return false

  return true
}

export const getCurrentBreakpoint = () => {
  const theme = useTheme()

  const breakpoints = [
    { name: 'xl', query: useMediaQuery(theme.breakpoints.up('xl')) },
    { name: 'lg', query: useMediaQuery(theme.breakpoints.up('lg')) },
    { name: 'md', query: useMediaQuery(theme.breakpoints.up('md')) },
    { name: 'sm', query: useMediaQuery(theme.breakpoints.up('sm')) }
  ]

  const found = breakpoints.find(bp => bp.query)
  return found ? found.name : 'xs'
}
